import "nprogress/nprogress.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import App from "src/App";
import { SidebarProvider } from "src/components/Context/SidebarContext";
import * as serviceWorker from "src/serviceWorker";
import { AuthContextProvider } from "./components/Context/AuthContext/AuthContext";
import { ModalContextProvider } from "./components/Context/ModalContext";

// Suppress benign ResizeObserver loop error from triggering CRA error overlay
const isResizeObserverError = (msg?: string) =>
	typeof msg === "string" &&
	(msg.includes("ResizeObserver loop completed with undelivered notifications") ||
		msg.includes("ResizeObserver loop limit exceeded"));

if (typeof window !== "undefined") {
	const originalOnError = window.onerror;
	window.onerror = (message, source, lineno, colno, error) => {
		if (typeof message === "string" && isResizeObserverError(message)) {
			return true;
		}
		if (originalOnError) {
			return originalOnError(message, source, lineno, colno, error);
		}
		return false;
	};

	window.addEventListener(
		"error",
		(e) => {
			if (isResizeObserverError(e.message)) {
				e.stopImmediatePropagation();
				e.stopPropagation();
				e.preventDefault();
			}
		},
		true
	);
}

const root = createRoot(document.getElementById("root")!);

const queryClient = new QueryClient({
	defaultOptions: {
		queries: { staleTime: 1000 * 60 * 5 },
	},
});

root.render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<HelmetProvider>
				<ModalContextProvider>
					<SidebarProvider>
						<BrowserRouter>
							<AuthContextProvider>
								<App />
							</AuthContextProvider>
						</BrowserRouter>
					</SidebarProvider>
				</ModalContextProvider>
			</HelmetProvider>
		</QueryClientProvider>
	</StrictMode>
);

serviceWorker.unregister();
